import { isCurrentFieldAcceptance } from '../field-current-evidence';

import { FieldStatus, FieldTime } from './field-primitives';

import type { FieldJourney, FieldSnapshot } from '@mio/shared';

const journeys: Record<FieldJourney, string> = {
  knowledge: 'Grounded knowledge',
  trade: 'Controlled trade execution',
  recovery: 'Operations and recovery',
  synthetic_cohort: 'Synthetic cohort business acceptance',
};

export function FieldControls({ snapshot }: { snapshot: FieldSnapshot }) {
  const controls = snapshot.controls;
  const actorName = (id: string) => snapshot.actors.find((actor) => actor.id === id)?.name ?? id;
  return (
    <section aria-label="Company decisions" className="border-line space-y-5 rounded-md border p-4">
      <div>
        <h3 className="text-sm font-medium">Company decisions</h3>
        <p className="text-fg-muted mt-1 text-sm leading-6">
          Share verification with Jordan for journey acceptance and Marcus for business acceptance.
          Elena owns control holds; Amara owns synthetic release decisions. Each decision keeps its
          source revision and evidence.
        </p>
      </div>
      <div>
        <h4 className="text-xs font-medium">Control holds</h4>
        {controls?.holds.length ? (
          <ol className="mt-2 space-y-3">
            {controls.holds.map((hold) => (
              <li
                key={hold.id}
                data-object-id={hold.id}
                tabIndex={-1}
                className="border-line rounded border p-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    {actorName(hold.actorId)} · <code>{hold.revision.slice(0, 12)}</code>
                  </span>
                  <FieldStatus value={hold.status === 'active' ? 'blocked' : 'resolved'} />
                </div>
                <p className="mt-2 whitespace-pre-wrap">{hold.reason}</p>
                <p className="text-fg-muted mt-1 text-xs">
                  Evidence:{' '}
                  {hold.evidence
                    .map((source) => `${source.id} v${source.revision.slice(0, 12)}`)
                    .join(', ')}
                </p>
                {hold.resolution ? (
                  <p className="text-fg-muted mt-2 text-xs">
                    Resolved against <code>{hold.resolution.revision.slice(0, 12)}</code>:{' '}
                    {hold.resolution.rationale}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-fg-muted mt-1 text-sm">No control holds recorded.</p>
        )}
      </div>
      <div>
        <h4 className="text-xs font-medium">Journey acceptance</h4>
        {controls?.acceptances.length ? (
          <ol className="mt-2 space-y-3">
            {controls.acceptances.map((acceptance) => {
              const current = isCurrentFieldAcceptance(snapshot, acceptance);
              return (
                <li
                  key={acceptance.id}
                  data-object-id={acceptance.id}
                  tabIndex={-1}
                  className="border-line rounded border p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>{journeys[acceptance.journey]}</span>
                    <FieldStatus value={current ? 'accepted' : 'previous_evidence'} />
                  </div>
                  <p className="text-fg-muted mt-1 text-xs">
                    {actorName(acceptance.actorId)} ·{' '}
                    <code>{acceptance.revision.slice(0, 12)}</code> ·{' '}
                    <FieldTime value={acceptance.createdAt} />
                  </p>
                  <p className="mt-2 whitespace-pre-wrap">{acceptance.rationale}</p>
                  <p className="text-fg-muted mt-1 text-xs">{acceptance.limitations}</p>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-fg-muted mt-1 text-sm">
            No journey acceptance recorded. Passing checks and colleague acceptance are both
            required.
          </p>
        )}
      </div>
      {controls?.actions.length ? (
        <div>
          <h4 className="text-xs font-medium">Synthetic release history</h4>
          <p className="text-fg-muted mt-1 text-xs">
            These decisions affect the company simulation. Recovery evidence remains preserved.
          </p>
          <ol className="mt-2 space-y-3">
            {controls.actions.map((action) => (
              <li
                key={action.id}
                data-object-id={action.id}
                tabIndex={-1}
                className="border-line rounded border p-3 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <FieldStatus value={action.action} />
                  <code className="text-xs">{action.revision.slice(0, 12)}</code>
                  <span className="text-fg-muted text-xs">
                    <FieldTime value={action.createdAt} />
                  </span>
                </div>
                <p className="mt-2 whitespace-pre-wrap">{action.reason}</p>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
